import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import TereikoIcon from '../../../public/logos/TereikoLogo.png';
import { useAuth } from '../../hooks/useAuth';
import { useImageUrl } from '../../services/uploads';
import type { ViewMode } from '../../contexts/viewMode.utils';

interface ModeTransitionOverlayProps {
  show: boolean;
  /** The mode being switched into — drives the status label. */
  target: ViewMode;
}

/**
 * Full-screen branded cover shown while switching view mode. It hides the
 * query-cache reset + route change behind a brief, deliberate transition so the
 * new surface never flashes half-loaded data. While visible it takes focus so a
 * keyboard user can't tab to the controls hidden beneath it, and it honors
 * `prefers-reduced-motion` by dropping the looping logo pulse.
 */
const ModeTransitionOverlay: React.FC<ModeTransitionOverlayProps> = ({ show, target }) => {
  // A branded workspace switches modes under its OWN mark, not Tereiko's
  // (Aleix, 2026-09-10) — the sidebar already does this.
  const { activeMembership } = useAuth();
  const [brandLogoUrl] = useImageUrl(activeMembership?.tenant.styling?.logo_key ?? null);
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  // Pull focus onto the cover so the obscured page controls aren't keyboard-
  // reachable for the duration; focus returns to the document on unmount.
  useEffect(() => {
    if (show) ref.current?.focus();
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          ref={ref}
          tabIndex={-1}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-6 bg-background-primary outline-none"
          role="status"
          aria-live="polite"
        >
          <motion.img
            src={brandLogoUrl ?? TereikoIcon}
            alt=""
            className="w-14 h-14 object-contain"
            animate={reduceMotion ? undefined : { scale: [1, 1.08, 1] }}
            transition={
              reduceMotion ? undefined : { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
            }
          />
          <div className="spinner spinner-md text-primary-600 dark:text-primary-350" aria-hidden="true" />
          <p className="text-sm font-medium text-text-secondary">
            Switching to {target === 'manage' ? 'Manage' : 'Learning'}…
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ModeTransitionOverlay;
